import { SuperInput } from "@components:SuperInput/SuperInput";
import { Box, CircularProgress, TextField } from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ColorPickerField } from "./ColorPickerField";
import { LogoUploadZone } from "./LogoUploadZone";
import { SectionWrapper } from "./SectionWrapper";
import { FieldLabel } from "./shared-styles";

interface SelectOption {
  value: string;
  label: string;
}

interface BrandingSelectFieldProps {
  name: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  options: SelectOption[];
  loading: boolean;
  placeholder: string;
  noOptionsText: string;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules?: RegisterOptions<any>;
  errorText?: string;
}

const BrandingSelectField = ({
  name,
  label,
  control,
  options,
  loading,
  placeholder,
  noOptionsText,
  disabled,
  rules,
  errorText,
}: BrandingSelectFieldProps) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <FieldLabel>{label}</FieldLabel>
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({ field }) => {
        const selectedValue =
          options.find((opt) => opt.value === field.value) ?? null;

        return (
          <SuperInput.Autocomplete
            sx={{ width: "100%" }}
            loading={loading}
            disabled={disabled}
            options={options}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            {...field}
            value={selectedValue}
            onChange={(_, data) => {
              field.onChange(data?.value || "");
            }}
            noOptionsText={noOptionsText}
            width="100%"
            errorText={errorText}
          />
        );
      }}
    />
  </Box>
);

interface CreateCoreSectionProps {
  organizationOptions: SelectOption[];
  isOrgLoading: boolean;
  botOptions: SelectOption[];
  isBotLoading: boolean;
  botDisabled: boolean;
  logoPreview: string | null;
  onLogoUpload: (file: File) => void;
  onLogoRemove: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
}

export const CreateCoreSection = ({
  organizationOptions,
  isOrgLoading,
  botOptions,
  isBotLoading,
  botDisabled,
  logoPreview,
  onLogoUpload,
  onLogoRemove,
  control,
  errors,
}: CreateCoreSectionProps) => {
  const { t } = useTranslation();
  return (
    <SectionWrapper
      instruction={t("preferences.uiBranding.instructions.coreCreate")}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Organization & Bot selection */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* Organization */}
          <BrandingSelectField
            name="organizationId"
            label={t("preferences.uiBranding.organizationFieldLabel")}
            control={control}
            options={organizationOptions}
            loading={isOrgLoading}
            placeholder={t(
              "preferences.uiBranding.selectOrganizationPlaceholder",
            )}
            noOptionsText={t("preferences.uiBranding.noOrganizations")}
            rules={{
              required: t("common.requiredField", {
                field: t("preferences.uiBranding.organization"),
              }),
            }}
            errorText={errors.organizationId?.message as string | undefined}
          />

          {/* Bot */}
          <BrandingSelectField
            name="botId"
            label={t("preferences.uiBranding.bot")}
            control={control}
            options={botOptions}
            loading={isBotLoading}
            disabled={botDisabled}
            placeholder={
              botDisabled
                ? t("preferences.uiBranding.selectOrganizationFirst")
                : t("preferences.uiBranding.all")
            }
            noOptionsText={t("preferences.uiBranding.noBots")}
          />
        </Box>

        <LogoUploadZone
          logoPreview={logoPreview}
          onUpload={onLogoUpload}
          onRemove={onLogoRemove}
        />

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Controller
            name="primaryColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorPrimary")}
              />
            )}
          />
          <Controller
            name="secondaryColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorSecondary")}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Controller
            name="sidebarColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorSidebar")}
              />
            )}
          />
          <Controller
            name="backgroundColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorBackground")}
              />
            )}
          />
        </Box>
      </Box>
    </SectionWrapper>
  );
};
